// tree traversal & mutation helpers for PresentationOutlineSection trees
export const getAtPath = <TSlide = OutlineSlide>(
  sections: PresentationOutlineSection<TSlide>[],
  path: number[],
): PresentationOutlineSection<TSlide> | null => {
  if (!path.length) return null;
  const root = sections[path[0]] ?? null;
  if (!root || path.length === 1) return root;

  let current = root;
  for (let i = 1; i < path.length; i++) {
    const item = current.items?.[path[i]];
    if (!item || item.kind !== 'section') return null;
    current = item.data;
  }
  return current;
};

export const updateAtPath = <TSlide = OutlineSlide>(
  sections: PresentationOutlineSection<TSlide>[],
  path: number[],
  updater: (s: PresentationOutlineSection<TSlide>) => PresentationOutlineSection<TSlide>,
): PresentationOutlineSection<TSlide>[] => {
  if (!path.length) return sections;
  return sections.map((s, i) => {
    if (i !== path[0]) return s;
    if (path.length === 1) return updater(s);
    const update = (section: PresentationOutlineSection<TSlide>, subPath: number[]): PresentationOutlineSection<TSlide> => {
      if (!subPath.length) return updater(section);
      return {
        ...section,
        items: (section.items ?? []).map((item, j) => {
          if (j !== subPath[0] || item.kind !== 'section') return item;
          return { kind: 'section' as const, data: update(item.data, subPath.slice(1)) };
        }),
      };
    };
    return update(s, path.slice(1));
  });
};

export const removeAtPath = <TSlide = OutlineSlide>(
  sections: PresentationOutlineSection<TSlide>[],
  path: number[],
): { result: PresentationOutlineSection<TSlide>[]; removed: PresentationOutlineSection<TSlide> | null } => {
  if (!path.length) return { result: sections, removed: null };

  if (path.length === 1) {
    const removed = sections[path[0]] ?? null;
    return { result: sections.filter((_, i) => i !== path[0]), removed };
  }

  let removed: PresentationOutlineSection<TSlide> | null = null;

  const result = sections.map((s, i) => {
    if (i !== path[0]) return s;
    return removeInItems(s, path.slice(1), (r) => {
      removed = r;
    });
  });

  return { result, removed };
};

const removeInItems = <TSlide = OutlineSlide>(
  section: PresentationOutlineSection<TSlide>,
  subPath: number[],
  onRemoved: (s: PresentationOutlineSection<TSlide>) => void,
): PresentationOutlineSection<TSlide> => {
  if (subPath.length === 1) {
    const item = section.items?.[subPath[0]];
    if (item?.kind === 'section') onRemoved(item.data);
    return { ...section, items: (section.items ?? []).filter((_, i) => i !== subPath[0]) };
  }
  return {
    ...section,
    items: (section.items ?? []).map((item, i) => {
      if (i !== subPath[0] || item.kind !== 'section') return item;
      return { kind: 'section' as const, data: removeInItems(item.data, subPath.slice(1), onRemoved) };
    }),
  };
};

// insert section at parentPath+itemIdx; parentPath=[] inserts at root level
export const insertAtPath = (
  sections: PresentationOutlineSection[],
  parentPath: number[],
  itemIdx: number,
  section: PresentationOutlineSection,
): PresentationOutlineSection[] => {
  if (parentPath.length === 0) {
    const result = [...sections];
    result.splice(itemIdx, 0, section);
    return result;
  }
  return updateAtPath(sections, parentPath, (s) => {
    const newItems = [...(s.items ?? [])];
    newItems.splice(itemIdx, 0, { kind: 'section', data: section });
    return { ...s, items: newItems };
  });
};

// height of this section's own subtree (0 = no nested sections)
export const subtreeHeight = (section: PresentationOutlineSection): number => {
  const childSections = (section.items ?? []).filter((item) => item.kind === 'section');
  if (!childSections.length) return 0;
  return 1 + Math.max(...childSections.map((item) => subtreeHeight(item.data)));
};

// Adjust a path after removing an item at removedPath.
// If the path is in the same parent subtree and comes after the removed index, decrement that index.
export const shiftPathAfterRemoval = (path: number[], removedPath: number[]): number[] => {
  const depth = removedPath.length - 1; // the level where removal happened
  if (path.length <= depth) return path;
  for (let i = 0; i < depth; i++) {
    if (path[i] !== removedPath[i]) return path; // different branch, unaffected
  }
  const removedIdx = removedPath[depth];
  const pathIdx = path[depth];
  if (pathIdx > removedIdx) {
    return [...path.slice(0, depth), pathIdx - 1, ...path.slice(depth + 1)];
  }
  return path;
};

// true if pathA is an ancestor of or equal to pathB
export const isAncestorOrSelf = (pathA: number[], pathB: number[]): boolean => {
  if (pathA.length > pathB.length) return false;
  return pathA.every((v, i) => v === pathB[i]);
};

export const flattenSectionSlides = <T>(
  sections: PresentationOutlineSection[],
  mapper: (slide: OutlineSlide, sectionPath: number[], itemIdx: number) => T,
  parentPath: number[] = [],
): T[] => {
  const results: T[] = [];
  const visit = (section: PresentationOutlineSection, sectionPath: number[]) => {
    (section.items ?? []).forEach((item, itemIdx) => {
      if (item.kind === 'slide') {
        results.push(mapper(item.data, sectionPath, itemIdx));
      } else {
        visit(item.data, [...sectionPath, itemIdx]);
      }
    });
  };
  sections.forEach((section, si) => visit(section, [...parentPath, si]));
  return results;
};

// recursively count active and total slides across the tree
export const countSectionSlides = (sections: PresentationOutlineSection[]): { active: number; total: number } => {
  let active = 0;
  let total = 0;

  const visit = (section: PresentationOutlineSection) => {
    (section.items ?? []).forEach((item) => {
      if (item.kind === 'slide') {
        total++;
        if (section.enabled !== false) active++;
      } else {
        visit(item.data);
      }
    });
  };

  sections.forEach(visit);
  return { active, total };
};

export const deepCloneSections = (sections: PresentationOutlineSection[]): PresentationOutlineSection[] =>
  sections.map((s) => ({
    ...s,
    items: (s.items ?? []).map((item) =>
      item.kind === 'slide' ? item : { kind: 'section' as const, data: deepCloneSections([item.data])[0] },
    ),
  }));
